<template>
  <div>
    <process-step
      v-bind:current="state.context.step"
      v-bind:number="1"
      v-slot="{ isCurrent, isPast }"
    >
      <div
        class="d-flex align-items-center justify-content-between"
        v-if="isCurrent"
      >
        <div>
          <div class="step-title">Select a file</div>
          <div>
            <input
              type="file"
              class="text-ellipsis"
              accept="application/pdf"
              v-on:change="send('SELECT', { file: $event.target.files[0] })"
            />
          </div>
        </div>
        <div>
          <button
            class="btn btn-primary"
            v-if="state.matches('extracting.selected')"
            v-on:click="send('UPLOAD')"
          >
            Upload
          </button>
        </div>
      </div>
      <div v-if="isPast">
        <div class="step-title">File selected</div>
        <div>The file has been selected and submitted.</div>
      </div>
    </process-step>
    <process-step
      v-bind:failed="state.matches('failed')"
      v-bind:current="state.context.step"
      v-bind:number="2"
      v-slot="{ isCurrent, isPast, isFuture, hasFailed }"
    >
      <div
        class="d-flex align-items-center justify-content-between"
        v-if="hasFailed"
      >
        <div>
          <div class="step-title">File upload failed</div>
          <template v-if="state.event.data.message === 'Network Error'">
            A network error occurred during upload.
          </template>
          <template v-else-if="state.event.data.message.includes('401')"
            >Current session has expired. Please sign in again.</template
          >
          <template
            v-else-if="
              state.event.data.response &&
                state.event.data.response.data.causes &&
                state.event.data.response.data.causes.includes('invalid_file')
            "
            >The uploaded file is either not a PDF or is corrupted.</template
          >
          <template
            v-else-if="
              state.event.data.response &&
                state.event.data.response.data.causes &&
                state.event.data.response.data.causes.includes('too_many_pages')
            "
          >
            The file can't be processed.
            <abbr
              title="Click to know."
              v-on:click="
                alert(
                  'The PDF contains images encoded using JPEG2000, which we can\'t process. You can try to convert it by printing to another file.'
                )
              "
              >Here's why.</abbr
            >
          </template>
          <template v-else>{{ state.event.data.message }}</template>
        </div>
        <div>
          <button class="btn btn-primary" v-on:click="send('RETRY')">
            Retry
          </button>
        </div>
      </div>
      <template v-else>
        <div v-if="isFuture">
          <div class="step-title">Upload a file</div>
          <div>Select a file and upload it to be analysed.</div>
        </div>
        <div v-if="isPast">
          <div class="step-title">File uploaded</div>
          <div>The file was uploaded to our servers.</div>
        </div>
        <div
          class="d-flex align-items-center justify-content-between"
          v-if="isCurrent"
        >
          <div>
            <div class="step-title">Uploading file...</div>
            <div>This may take a few seconds.</div>
          </div>
          <div
            class="spinner-border spinner-border-sm text-secondary"
            role="status"
          ></div>
        </div>
      </template>
    </process-step>
    <process-step
      v-bind:failed="state.matches('failed')"
      v-bind:current="state.context.step"
      v-bind:number="3"
      v-slot="{ isCurrent, isPast, isFuture, hasFailed }"
    >
      <div
        class="d-flex align-items-center justify-content-between"
        v-if="hasFailed"
      >
        <div>
          <div class="step-title">File processing failed</div>
          <template v-if="state.event.data.message === 'Network Error'">
            A network error occurred during processing.
          </template>
          <template v-else>{{ state.event.data.message }}</template>
        </div>
        <div>
          <button class="btn btn-primary" v-on:click="send('RETRY')">
            Retry
          </button>
        </div>
      </div>
      <template v-else>
        <template v-if="isFuture">
          <div class="step-title">File processing</div>
          <div>We will convert the content of the PDF to text.</div>
        </template>
        <div v-if="isPast">
          <div class="step-title">File processed</div>
          <div>We converted the content of the PDF to text.</div>
        </div>
        <div
          class="d-flex align-items-center justify-content-between"
          v-if="isCurrent"
        >
          <div>
            <div class="step-title">Processing file...</div>
            <div>It may take a few minutes.</div>
          </div>
          <div
            class="spinner-border spinner-border-sm text-secondary"
            role="status"
          ></div>
        </div>
      </template>
    </process-step>
    <process-step
      v-bind:failed="state.matches('failed')"
      v-bind:current="state.context.step"
      v-bind:number="4"
      v-slot="{ isCurrent, isPast, isFuture, hasFailed }"
    >
      <div
        class="d-flex align-items-center justify-content-between"
        v-if="hasFailed"
      >
        <div>
          <div class="step-title">Document analysis failed</div>
          <template v-if="state.event.data.message === 'Network Error'">
            A network error occurred during analysis.
          </template>
          <template v-else>{{ state.event.data.message }}</template>
        </div>
        <div>
          <button class="btn btn-primary" v-on:click="send('RETRY')">
            Retry
          </button>
        </div>
      </div>
      <template v-else>
        <template v-if="isFuture">
          <div class="step-title">Document analysis</div>
          <div>We will analyse the content of the documents.</div>
        </template>
        <div v-if="isPast">
          <div class="step-title">Document analysed</div>
          <div>We analysed the content of the documents.</div>
        </div>
        <div
          class="d-flex align-items-center justify-content-between"
          v-if="isCurrent"
        >
          <div>
            <div class="step-title">Analysing document...</div>
            <div>It may take a few seconds.</div>
          </div>
          <div
            class="spinner-border spinner-border-sm text-secondary"
            role="status"
          ></div>
        </div>
      </template>
    </process-step>
  </div>
</template>

<script>
import ProcessStep from "@/components/steps/ProcessStep";

export default {
  name: "ProcessSteps",
  components: {
    ProcessStep
  },
  props: {
    state: { type: Object, required: true },
    send: { type: Function, required: true }
  },
  methods: {
    alert(message) {
      window.alert(message);
    }
  }
};
</script>

<style>
.text-ellipsis {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: calc(100%);
}
</style>
